import { useState, useEffect } from "react";
import { authFetch } from "../api";

const TODAY = new Date().toISOString().slice(0, 10);

const FIELDS = [
  {
    section: "Consumables",
    icon: "💧",
    items: [
      { key: "water_cans",  label: "Water cans (20L)",     unit: "cans",    type: "int" },
      { key: "ors_packets", label: "ORS packets",           unit: "packets", type: "int" },
      { key: "care_kits",   label: "Care kits distributed", unit: "kits",    type: "int" },
    ],
  },
  {
    section: "Training",
    icon: "📋",
    items: [
      { key: "trainings",              label: "Training sessions held", unit: "sessions", type: "int" },
      { key: "members_trained_male",   label: "Members trained — Male",   unit: "people", type: "int" },
      { key: "members_trained_female", label: "Members trained — Female", unit: "people", type: "int" },
    ],
  },
];

export default function DailyLog({ center, userEmail, apiBase, onClose }) {
  const [date, setDate]     = useState(TODAY);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchLog = async (d) => {
    setLoading(true);
    try {
      const res = await authFetch(`${apiBase}/centers/${center.id}/daily-log?date=${d}`);
      if (res.ok) {
        const data = await res.json();
        // Strip nulls so inputs show as blank
        const cleaned = {};
        Object.entries(data).forEach(([k, v]) => {
          if (v !== null && v !== undefined && !["center_id","date","recorded_by","updated_at"].includes(k)) {
            cleaned[k] = v;
          }
        });
        setValues(cleaned);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLog(date); }, [date]);

  const handleChange = (key, raw, type) => {
    const val = raw === "" ? undefined : type === "float" ? parseFloat(raw) : parseInt(raw, 10);
    setValues(v => ({ ...v, [key]: val }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${apiBase}/centers/${center.id}/daily-log?date=${date}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ center_id: center.id, date, ...values }),
      });
      if (!res.ok) throw new Error();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      alert("Failed to save — please try again");
    } finally {
      setSaving(false);
    }
  };

  const isModal = !!onClose;

  return (
    <div style={isModal ? styles.overlay : {}}>
      <div style={isModal ? styles.card : styles.cardInline}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>Daily Log</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>{center.name}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="date"
              value={date}
              max={TODAY}
              onChange={e => { setDate(e.target.value); setSaved(false); }}
              style={styles.datePicker}
            />
            {onClose && (
              <button onClick={onClose} style={styles.closeBtn}>✕</button>
            )}
          </div>
        </div>

        {/* Saved flash */}
        {saved && (
          <div style={styles.savedBanner}>Saved for {date}</div>
        )}

        <div style={styles.body}>
          {loading ? (
            <div style={{ color: "#888", textAlign: "center", padding: 24 }}>Loading…</div>
          ) : (
            FIELDS.map(section => (
              <div key={section.section} style={styles.section}>
                <div style={styles.sectionLabel}>
                  {section.icon} {section.section}
                </div>
                {section.items.map(field => (
                  <div key={field.key} style={styles.row}>
                    <label style={styles.label}>{field.label}</label>
                    <div style={styles.inputWrap}>
                      <input
                        type="number"
                        min="0"
                        step={field.type === "float" ? "0.1" : "1"}
                        value={values[field.key] ?? ""}
                        onChange={e => handleChange(field.key, e.target.value, field.type)}
                        placeholder="—"
                        style={styles.input}
                      />
                      <span style={styles.unit}>{field.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        <button
          onClick={handleSave}
          disabled={saving || loading}
          style={styles.saveBtn(saving || loading)}
        >
          {saving ? "Saving…" : "Save Daily Log"}
        </button>

      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: 16,
  },
  card: {
    background: "#fff", borderRadius: 16,
    width: "100%", maxWidth: 440,
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    maxHeight: "92vh", display: "flex", flexDirection: "column",
    overflow: "hidden",
  },
  cardInline: {
    background: "#fff", borderRadius: 16,
    width: "100%", display: "flex", flexDirection: "column",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  header: {
    background: "#004d74", color: "#fff",
    padding: "14px 20px",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    flexShrink: 0,
  },
  datePicker: {
    padding: "5px 8px", borderRadius: 7,
    border: "none", fontSize: 13,
    background: "rgba(255,255,255,0.15)", color: "#fff",
    cursor: "pointer",
  },
  closeBtn: {
    background: "none", border: "none",
    fontSize: 18, cursor: "pointer", color: "#fff", opacity: 0.8,
  },
  savedBanner: {
    background: "#e0f3fc", color: "#004d74",
    padding: "8px 20px", fontSize: 13, fontWeight: 600,
    textAlign: "center", flexShrink: 0,
  },
  body: {
    padding: "16px 20px",
    overflowY: "auto", flex: 1,
  },
  section: { marginBottom: 20 },
  sectionLabel: {
    fontSize: 12, fontWeight: 700, color: "#555",
    textTransform: "uppercase", letterSpacing: "0.05em",
    marginBottom: 10,
  },
  row: {
    display: "flex", justifyContent: "space-between",
    alignItems: "center", marginBottom: 10,
  },
  label: { fontSize: 14, color: "#333", flex: 1 },
  inputWrap: {
    display: "flex", alignItems: "center", gap: 6,
  },
  input: {
    width: 80, padding: "7px 10px",
    border: "1px solid #ddd", borderRadius: 8,
    fontSize: 15, textAlign: "right",
    outline: "none",
  },
  unit: { fontSize: 12, color: "#888", minWidth: 44 },
  saveBtn: (disabled) => ({
    margin: "0 20px 20px",
    padding: "13px 0", borderRadius: 10,
    background: disabled ? "#d0d0d0" : "#0093dd",
    color: "#fff", border: "none",
    fontSize: 15, fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    flexShrink: 0,
  }),
};
